import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency } from '../utils/constants';
import { RefreshCw, TrendingUp } from 'lucide-react';

const Backtest = () => {
  const { post, loading } = useApi();
  const [result, setResult] = useState(null);
  const [riskProfile, setRiskProfileInput] = useState('MODERATE');

  const handleBacktest = async () => {
    try {
      const data = await post('/ml/backtest', { risk_profile: riskProfile });
      setResult(data);
    } catch { /* handled */ }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-700">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <RefreshCw className="h-8 w-8 text-teal-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Portfolio Backtest</h1>
          <p className="text-gray-500">Compare optimized portfolio vs equal-weight benchmark</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div>
            <label className="label">Risk Profile</label>
            <select
              className="input-field"
              value={riskProfile}
              onChange={(e) => setRiskProfileInput(e.target.value)}
            >
              <option value="CONSERVATIVE">Conservative</option>
              <option value="MODERATE">Moderate</option>
              <option value="AGGRESSIVE">Aggressive</option>
            </select>
          </div>
          <button onClick={handleBacktest} className="btn-primary mt-5" disabled={loading}>
            {loading ? 'Running...' : 'Run Backtest'}
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-6">
          {/* Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="card text-center">
              <p className="text-sm text-gray-500">Portfolio CAGR</p>
              <p className="text-2xl font-bold text-green-600">
                {(result.portfolio_cagr * 100).toFixed(1)}%
              </p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">Benchmark CAGR</p>
              <p className="text-2xl font-bold text-gray-600">
                {(result.benchmark_cagr * 100).toFixed(1)}%
              </p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">Alpha</p>
              <p className={`text-2xl font-bold ${result.alpha >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {result.alpha >= 0 ? '+' : ''}{(result.alpha * 100).toFixed(1)}%
              </p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">Data Points</p>
              <p className="text-2xl font-bold text-primary-600">{result.portfolio_values?.length || 0}</p>
            </div>
          </div>

          {/* Chart */}
          {result.portfolio_values && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-600" /> Portfolio vs Benchmark
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.portfolio_values.map((pv, idx) => ({
                    date: result.dates?.[idx] || idx,
                    portfolio: pv,
                    benchmark: result.benchmark_values?.[idx] || 0,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => formatCurrency(v)} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="portfolio" name="Optimized Portfolio" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="benchmark" name="Equal-Weight" stroke="#94a3b8" strokeWidth={2} dot={false} strokeDasharray="5 5" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Backtest;
